'use client'

import { useMutation } from '@apollo/client'
import { Button, Label, RadioGroup, RadioGroupItem } from '@chain-zen/ui'
import { useEffect } from 'react'
import { toast } from 'sonner'

import { useWorkspaceSetup } from '../../contexts/setup-workspace-context'
import { SEND_VERIFICATION_CODE } from '../../data/mutations/workspace'
import { useSetupWorkspaceModal } from '../../stores/setup-workspace-modal-store'
import { ReceiveCodeMethodEnum, VerificationMethodEnum } from '../../types/company'
import { SetupWorkspaceLayout } from './setup-workspace-layout'

// generate the text for the verification method
const generateVerificationOptionText = ({ type, value }: { type: string; value: string }) => {
  const messages: Record<string, string> = {
    [VerificationMethodEnum.Email]: `Send a code to ${value}`,
    [VerificationMethodEnum.Phone]: `Call/text a code to ${value}`,
    [VerificationMethodEnum.CompanyEmail]: `Send a code to a "@${value}" email`,
    [VerificationMethodEnum.ComplianceEmail]: `Select this if Chain Compliance asked you to`
  }

  return messages[type] || value
}

/**
 * This function is used to get the receive code method for the verification method.
 */
const getReceiveCodeMethod = (type: VerificationMethodEnum): ReceiveCodeMethodEnum => {
  const methods: Record<VerificationMethodEnum, ReceiveCodeMethodEnum> = {
    [VerificationMethodEnum.Email]: ReceiveCodeMethodEnum.Email,
    [VerificationMethodEnum.Phone]: ReceiveCodeMethodEnum.SMS,
    [VerificationMethodEnum.CompanyEmail]: ReceiveCodeMethodEnum.Email,
    [VerificationMethodEnum.ComplianceEmail]: ReceiveCodeMethodEnum.Email,
    [VerificationMethodEnum.Custom]: ReceiveCodeMethodEnum.SMS
  }

  return methods[type] ?? ReceiveCodeMethodEnum.SMS
}

/**
 * In this step, the user selects how they want to be verified - email, phone, or company email.
 */
export const SelectVerificationMethod = () => {
  const {
    allowableVerificationMethods,
    verificationMethod,
    receiveCodeMethod,
    setStep,
    setVerificationMethod,
    setReceiveCodeMethod
  } = useWorkspaceSetup()

  const { workspaceID } = useSetupWorkspaceModal()

  // set the verification method and receive code method on the first render
  useEffect(() => {
    const firstMethod = allowableVerificationMethods?.verificationMethods?.[0]

    if (!firstMethod) return

    setVerificationMethod({
      type: firstMethod.type as VerificationMethodEnum,
      value: firstMethod.value
    })

    setReceiveCodeMethod(getReceiveCodeMethod(firstMethod.type as VerificationMethodEnum))
  }, [allowableVerificationMethods?.verificationMethods, setReceiveCodeMethod, setVerificationMethod])

  // handle the change of the verification method
  const handleChange = (event: string) => {
    const verificationType = allowableVerificationMethods?.verificationMethods?.find((method) => method.value === event)

    setReceiveCodeMethod(getReceiveCodeMethod(verificationType?.type as VerificationMethodEnum))

    setVerificationMethod({
      type: verificationType?.type ?? VerificationMethodEnum.Custom,
      value: event
    })
  }

  // sending code mutation
  const [sendVerificationCode, { loading, error }] = useMutation(SEND_VERIFICATION_CODE, {
    onCompleted: () => {
      setStep(8) // Setting the step to 8 will trigger the next step in the setup process which is the VerificationOTP component.
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const handleClick = async () => {
    if (
      verificationMethod?.type === VerificationMethodEnum.Email ||
      verificationMethod?.type === VerificationMethodEnum.ComplianceEmail
    ) {
      const input = {
        method: receiveCodeMethod,
        type: verificationMethod?.type
      }
      await sendVerificationCode({
        context: {
          headers: {
            'ws-id': workspaceID || ''
          }
        },
        variables: input
      })
    } else {
      if (verificationMethod?.type === VerificationMethodEnum.Phone) {
        setStep(6) // Setting the step to 6 will trigger the next step in the setup process which is the ReceiveCodeMethodForPhone component.
      } else if (verificationMethod?.type === VerificationMethodEnum.CompanyEmail) {
        setStep(7) // Setting the step to 7 will trigger the next step in the setup process which is the EmailAddress component.
      } else {
        setStep(9) // Setting the step to 9 will trigger the next step in the setup process which is the RequestCustomVerification component.
      }
    }
  }

  return (
    <SetupWorkspaceLayout
      title="How should we verify you?"
      subtitle="Choose how you would like to receive a temporary 6-digit code. This information is based on the information on file with the US DOT."
    >
      <RadioGroup
        onValueChange={handleChange}
        value={verificationMethod?.value}
        className="mt-4"
        id="dotOrMC"
        name="dotOrMC"
      >
        {allowableVerificationMethods?.verificationMethods?.map((method) => (
          <div className="flex items-center space-x-2 my-2" key={method.value}>
            <RadioGroupItem value={method.value} id={method.value} />
            <Label htmlFor={method.value}>{generateVerificationOptionText(method)}</Label>
          </div>
        ))}
        <div className="flex items-center space-x-2 my-2">
          <RadioGroupItem value="other" id="other" />
          <Label htmlFor="other">I need to be verified a different way 🤔</Label>
        </div>
      </RadioGroup>
      {!!error?.message && <Label className="text-red-500">{error?.message}</Label>}
      <Button className="w-full mt-4" loading={loading} onClick={handleClick}>
        Next
      </Button>
    </SetupWorkspaceLayout>
  )
}
